import {
  recognitionMetricKeys,
  type ExpExactEvidence,
  type ExpAdmission,
  type GameStateSnapshot,
  type MetricKey,
  type MetricReading,
  type RecognitionMetricKey,
  type RawReading,
  type ValidatedObservation,
} from '@/domain/types'
import type { GamePreset } from '@/presets/types'
import { normalizeJobName } from './job-name'
import { ExpAdmissionFilter, type ExpAdmissionCheckpoint } from './exp-admission'
import {
  enrichExpRequirement,
  expMatchesRequirement,
  ExpRequirementEstimator,
} from './exp-requirement-estimator'

type PreviousReadings = { -readonly [Key in RecognitionMetricKey]?: RawReading[Key] }

interface TrustedExpEvidencePoint {
  readonly level: number
  readonly absolute: number
  readonly ratio: number
  readonly requiredExp: number | null
  readonly sourceAt: number
}

interface ExactEvidenceDecision {
  readonly accepted: boolean
  readonly evidence?: ExpExactEvidence
}

export class ReadingValidator {
  private readonly expAdmission = new ExpAdmissionFilter()
  private previous: PreviousReadings = {}
  private pendingLevelIncrease: { from: number; to: number; capturedAt: number } | null = null
  private trustedExactExp: TrustedExpEvidencePoint | null = null
  private trustedAdmissionEpoch = 0
  private readonly expRequirements = new ExpRequirementEstimator()
  private mesosNeedsBaseline = false
  private mesosContinuityId: number | null = null
  private confirmedExpRebaseline = false
  private confirmedExactExpRebaseline = false

  constructor(
    private readonly preset: GamePreset,
    private minimumConfidence = 0.62,
  ) {}

  setMinimumConfidence(value: number): void {
    this.minimumConfidence = value
  }

  resetMetricContinuity(metrics: readonly MetricKey[]): void {
    if (metrics.includes('exp')) this.resetExpContinuity()
    for (const metric of metrics) delete this.previous[metric]
    if (metrics.includes('mesos')) {
      this.mesosNeedsBaseline = true
      this.mesosContinuityId = null
    }
  }

  resetExpContinuity(): void {
    this.expAdmission.breakContinuity()
  }

  expCheckpoint(): ExpAdmissionCheckpoint { return this.expAdmission.checkpoint() }

  restoreExpCheckpoint(checkpoint: ExpAdmissionCheckpoint): void {
    this.expAdmission.restore(checkpoint)
    this.trustedAdmissionEpoch = checkpoint.epoch
    const anchor = checkpoint.anchor
    this.trustedExactExp = anchor && anchor.level !== null ? {
      level: anchor.level, absolute: anchor.value.absolute!, ratio: anchor.value.ratio!,
      requiredExp: anchor.value.requiredExp ?? null, sourceAt: anchor.sourceAt,
    } : null
    this.expRequirements.reset(anchor?.level != null ? { level: anchor.level, value: anchor.value } : null)
  }

  validate(reading: RawReading): ValidatedObservation {
    this.confirmedExpRebaseline = false
    this.confirmedExactExpRebaseline = false
    if (this.pendingLevelIncrease && reading.capturedAt - this.pendingLevelIncrease.capturedAt > 15_000) {
      this.pendingLevelIncrease = null
    }
    if (reading.mesosContinuityId !== undefined && reading.mesosContinuityId !== this.mesosContinuityId) {
      this.mesosContinuityId = reading.mesosContinuityId
      this.mesosNeedsBaseline = true
      delete this.previous.mesos
    }
    if (reading.mesos.availability === 'unavailable') this.mesosNeedsBaseline = true
    const previousLevel = this.previous.level?.value
    const previousExpRatio = this.previous.exp?.value?.ratio
    const decisions = new Map<number, ExpAdmission>()
    const samples = [reading.expExactEvidence, reading.expExactCandidate
      ? { ...reading.expExactCandidate, confirmation: 'source-frame' as const } : undefined]
      .filter((sample): sample is ExpExactEvidence => sample !== undefined)
      .sort((a, b) => a.sourceAt - b.sourceAt)
    for (const sample of samples) decisions.set(sample.sourceAt, this.expAdmission.observe(sample, this.minimumConfidence))
    const candidateAdmission = reading.expExactCandidate ? decisions.get(reading.expExactCandidate.sourceAt) : undefined
    const evidenceAdmission = reading.expExactEvidence ? decisions.get(reading.expExactEvidence.sourceAt) : undefined
    const exactDecision = evidenceAdmission?.status === 'accepted'
      ? this.acceptExpExactEvidence({ ...reading.expExactEvidence!, value: evidenceAdmission.value! }, evidenceAdmission)
      : { accepted: false }
    const preparedReading = this.withObservedExpRequirement({
      ...reading,
      ...(typeof reading.jobName?.value === 'string'
        ? { jobName: { ...reading.jobName, value: normalizeJobName(reading.jobName.value) } }
        : {}),
    }, exactDecision.evidence)
    const accepted = Object.fromEntries(recognitionMetricKeys.map((key) => [key, this.acceptMetric(key, preparedReading)])) as Record<RecognitionMetricKey, boolean>
    const currentAdmission = samples.length ? decisions.get(samples.at(-1)!.sourceAt) : undefined
    const estimated = reading.exp.value && (reading.exp.value.estimated || reading.exp.value.absolute === null)
    const expQuarantined = this.expAdmission.isPending() || (currentAdmission !== undefined && currentAdmission.status !== 'accepted')
      || Boolean(estimated && !this.expAdmission.permitsEstimate(reading.level.value, reading.exp.value!.ratio, reading.exp.capturedAt))
    if (expQuarantined) accepted.exp = false
    const observation: ValidatedObservation = {
      ...preparedReading,
      accepted,
      expCandidateAdmission: candidateAdmission,
      expEvidenceAdmission: evidenceAdmission,
      expQuarantined,
      ...(reading.expExactEvidence ? { expExactEvidenceAccepted: exactDecision.accepted } : {}),
      ...(this.confirmedExpRebaseline ? { expRebaseline: true as const } : {}),
      ...(this.confirmedExactExpRebaseline ? { expExactRebaseline: true as const } : {}),
    }
    if (accepted.level && typeof previousLevel === 'number' && preparedReading.level.value === previousLevel + 1) {
      this.pendingLevelIncrease = { from: previousLevel, to: preparedReading.level.value, capturedAt: preparedReading.capturedAt }
    }
    if (accepted.exp && typeof previousExpRatio === 'number' && preparedReading.exp.value?.ratio !== null && preparedReading.exp.value!.ratio! < previousExpRatio - 0.08) {
      this.pendingLevelIncrease = null
    }
    if (observation.expRebaseline) this.pendingLevelIncrease = null
    if (accepted.level) this.previous.level = preparedReading.level
    if (accepted.exp) this.previous.exp = preparedReading.exp
    if (accepted.hp) this.previous.hp = preparedReading.hp
    if (accepted.mp) this.previous.mp = preparedReading.mp
    if (accepted.mesos) {
      this.previous.mesos = preparedReading.mesos
      this.mesosNeedsBaseline = false
    }
    if (accepted.mapName) this.previous.mapName = preparedReading.mapName
    if (accepted.jobName) this.previous.jobName = preparedReading.jobName
    return observation
  }

  resetBaseline(state: GameStateSnapshot | null = null): void {
    this.expAdmission.reset(state)
    this.trustedAdmissionEpoch = 0
    this.previous = state ? {
      ...(state.level === null ? {} : { level: this.seedReading('level', state.level, state) }),
      ...(state.exp.absolute === null && state.exp.ratio === null ? {} : { exp: this.seedReading('exp', state.exp, state) }),
      ...(state.hp.current === null && state.hp.ratio === null ? {} : { hp: this.seedReading('hp', state.hp, state) }),
      ...(state.mp.current === null && state.mp.ratio === null ? {} : { mp: this.seedReading('mp', state.mp, state) }),
      ...(state.mesos === null ? {} : { mesos: this.seedReading('mesos', state.mesos, state) }),
      ...(state.mapName === null ? {} : { mapName: this.seedReading('mapName', state.mapName, state) }),
    } : {}
    this.pendingLevelIncrease = null
    this.trustedExactExp = state ? this.exactEvidencePoint(state) : null
    this.expRequirements.reset(state?.level === null || state?.level === undefined
      ? null
      : { level: state.level, value: state.exp })
    this.mesosNeedsBaseline = false
    this.mesosContinuityId = null
  }

  private acceptExpExactEvidence(evidence: ExpExactEvidence | undefined, admission?: ExpAdmission): ExactEvidenceDecision {
    if (!evidence || admission?.status !== 'accepted' || evidence.level === null
      || evidence.value.absolute === null || evidence.value.ratio === null) return { accepted: false }
    const previous = this.trustedExactExp
    if (previous && evidence.sourceAt <= previous.sourceAt) return { accepted: false }
    this.expRequirements.seed(evidence.level, evidence.value)
    const point: TrustedExpEvidencePoint = {
      level: evidence.level, absolute: evidence.value.absolute, ratio: evidence.value.ratio,
      requiredExp: evidence.value.requiredExp ?? null, sourceAt: evidence.sourceAt,
    }
    const replacement = admission.rebaseline && (!previous || previous.sourceAt < admission.rebaseline.sourceAt)
    const epochChanged = admission.accounting !== undefined && admission.accounting.epoch !== this.trustedAdmissionEpoch
    if (admission.accounting) this.trustedAdmissionEpoch = admission.accounting.epoch
    const missingLevels = previous && (point.level < previous.level || point.level > previous.level + 1)
    if (replacement || missingLevels || epochChanged) {
      this.confirmedExactExpRebaseline = true
      this.confirmedExpRebaseline = true
    }
    this.trustedExactExp = point
    return { accepted: true, evidence }
  }

  private exactEvidencePoint(state: GameStateSnapshot): TrustedExpEvidencePoint | null {
    if (state.level === null || state.exp.absolute === null || state.exp.ratio === null || state.exp.estimated) return null
    const required = state.exp.requiredExp ?? null
    if (state.exp.absolute < 0
      || state.exp.ratio < 0
      || state.exp.ratio > 1
      || !expMatchesRequirement(state.exp, required)) {
      return null
    }
    return {
      level: state.level,
      absolute: state.exp.absolute,
      ratio: state.exp.ratio,
      requiredExp: required,
      sourceAt: state.observedAt?.exp ?? state.capturedAt,
    }
  }

  private withObservedExpRequirement(reading: RawReading, evidence?: ExpExactEvidence): RawReading {
    const level = evidence?.level ?? (typeof reading.level.value === 'number' ? reading.level.value : null)
    const value = reading.exp.value
    if (level === null || value === null) {
      return evidence ? { ...reading, expExactEvidence: evidence } : reading
    }
    const observed = evidence?.value.requiredExp
    const known = observed !== undefined ? observed : this.expRequirements.requirementFor(level)
    if (known === undefined) return evidence ? { ...reading, expExactEvidence: evidence } : reading
    const exp = {
      ...reading.exp,
      value: enrichExpRequirement({ ...value,
        requiredExpRange: evidence?.value.requiredExpRange ?? value.requiredExpRange,
        percentagePrecision: evidence?.value.percentagePrecision ?? value.percentagePrecision,
      }, known, known !== null),
    }
    return { ...reading, exp, ...(evidence ? { expExactEvidence: evidence } : {}) }
  }

  private seedReading<Key extends MetricKey>(metric: Key, value: NonNullable<RawReading[Key]['value']>, state: GameStateSnapshot): MetricReading<typeof value> {
    return {
      value,
      confidence: state.confidence[metric],
      source: this.preset.capabilities[metric] === 'ocr' ? 'ocr' : 'fused',
      availability: 'available',
      capturedAt: state.observedAt?.[metric] ?? state.capturedAt,
    }
  }

  private acceptMetric(metric: RecognitionMetricKey, reading: RawReading): boolean {
    if (this.preset.capabilities[metric] === 'unavailable') return false

    if (metric === 'level') {
      const candidate = reading.level
      const previous = this.previous.level
      if (candidate.availability === 'unavailable' || candidate.value === null || candidate.confidence < this.minimumConfidence) return false
      const value = candidate.value as number
      if (!Number.isInteger(value) || value < 1 || value > 200) return false
      if (previous?.value === null || previous?.value === undefined) return candidate.confirmation === 'consensus'
      if (value === previous.value) return true
      return candidate.confirmation === 'consensus'
    }

    if (metric === 'mesos') {
      const candidate = reading.mesos
      const previous = this.previous.mesos
      if (candidate.availability === 'unavailable' || candidate.value === null || candidate.confidence < this.minimumConfidence) return false
      const value = candidate.value as number
      if (!Number.isSafeInteger(value) || value < 0) return false
      if (this.mesosNeedsBaseline) return true
      if (previous?.value === null || previous?.value === undefined) return true
      return true
    }

    if (metric === 'mapName') {
      const candidate = reading.mapName
      if (candidate.availability === 'unavailable' || candidate.value === null || candidate.confidence < this.minimumConfidence) return false
      const value = candidate.value.trim()
      return value.length >= 2 && value.length <= 32 && /[\u3400-\u9fffA-Za-z0-9]/.test(value)
    }

    if (metric === 'jobName') {
      const candidate = reading.jobName
      if (candidate.availability === 'unavailable' || candidate.value === null || candidate.confidence < this.minimumConfidence) return false
      return normalizeJobName(candidate.value) !== null
    }

    if (metric === 'exp') {
      const candidate = reading.exp
      const previous = this.previous.exp
      if (previous && candidate.capturedAt <= previous.capturedAt) return false
      if (candidate.availability === 'unavailable' || candidate.value === null || candidate.confidence < this.minimumConfidence) return false
      const value = candidate.value
      if (value.ratio !== null && (value.ratio < 0 || value.ratio > 1)) return false
      if (value.absolute !== null && value.absolute < 0) return false
      const level = reading.level.value
      const simultaneousLevelUp = typeof level === 'number'
        && typeof this.previous.level?.value === 'number'
        && level === this.previous.level.value + 1
      const pendingLevelUp = this.pendingLevelIncrease !== null
        && level === this.pendingLevelIncrease.to
        && reading.capturedAt - this.pendingLevelIncrease.capturedAt <= 15_000
      const levelUpSupported = simultaneousLevelUp || pendingLevelUp
      if (value.absolute !== null && value.ratio !== null) {
        const zeroEndpoint = value.absolute === 0 && value.ratio === 0
        const exactIsConfirmed = zeroEndpoint
          ? levelUpSupported || value.requiredExp !== undefined
          : value.requiredExp !== undefined && expMatchesRequirement(value, value.requiredExp ?? null)
        if (!exactIsConfirmed) return false
      }
      const previousRatio = previous?.value?.ratio
      const previousAbsolute = previous?.value?.absolute
      const ratioReset = typeof previousRatio === 'number'
        && value.ratio !== null
        && (value.ratio < previousRatio - 0.018 || (value.ratio <= 0.001 && previousRatio > 0.001))
      const absoluteReset = typeof previousAbsolute === 'number'
        && value.absolute !== null
        && value.absolute < previousAbsolute
      if (ratioReset || absoluteReset) {
        if (!levelUpSupported && !this.confirmedExactExpRebaseline) return false
        if (!levelUpSupported) this.confirmedExpRebaseline = true
      }
      if (candidate.source === 'ocr' && reading.diagnostics.metrics?.exp?.status === 'confirming') return false
      return true
    }

    const candidate = metric === 'hp' ? reading.hp : reading.mp
    if (candidate.availability === 'unavailable' || candidate.value === null || candidate.confidence < this.minimumConfidence) return false
    const value = candidate.value
    if (value.ratio !== null && (value.ratio < 0 || value.ratio > 1)) return false
    if (value.current !== null && value.current < 0) return false
    if (value.max !== null && (value.max <= 0 || (value.current !== null && value.current > value.max * 1.05))) return false
    return true
  }

}
